import type { FinanceTranslations } from './types'

/**
 * English translations for Finance domain
 * Comprehensive translations covering all UI text
 */
export const enTranslations: FinanceTranslations = {
  // Page/Section level
  title: "Finance",
  subtitle: "Manage financial operations",
  overview: "Financial Overview",
  invoices: "Invoices",
  invoicesDescription: "View and manage invoices",

  // Actions
  addFinance: "Add Finance Record",
  editFinance: "Edit Finance Record",
  deleteFinance: "Delete Finance Record",
  print: "Print",
  downloadReceipt: "Download Receipt",

  // Search and filters
  searchPlaceholder: "Search transactions...",
  noItemsFound: "No finance records found",
  noItemsDescription: "You haven't added any finance records yet.",
  addFirst: "Add Your First Finance Record",

  // Transactions
  transactions: "Transactions",
  transactionsSubtitle: "View and manage all transactions",
  transactionHistory: "Transaction History",
  transactionNumber: "Transaction #",
  transactionDetails: "Transaction Details",
  noTransactions: "No Transactions",
  noTransactionsDescription: "No transactions match your current filters.",
  totalInvoiced: "Total Invoiced",
  totalPaid: "Total Paid",
  outstanding: "Outstanding",
  loadingCharge: "Loading Charge",
  paymentReceived: "Payment Received",

  // Transaction types
  type: "Type",
  payment: "Payment",
  invoice: "Invoice",
  credit: "Credit",
  debit: "Debit",
  completed: "Completed",
  failed: "Failed",
  processing: "Processing",

  // Transaction details
  description: "Description",
  noDescription: "No description provided",
  date: "Date",
  amount: "Amount",
  reference: "Reference",
  balanceAfter: "Balance After",
  actions: "Actions",

  // Statement
  statement: "Statement",
  statementSubtitle: "Account statement and balance history",
  noStatement: "No Statement",
  noStatementDescription: "No statement data available for the selected period.",
  openingBalance: "Opening Balance",
  closingBalance: "Closing Balance",
  netChange: "Net Change",

  // Payments
  paymentsTitle: "Payments",
  paymentsSubtitle: "View and track all payments",
  makePayment: "Make Payment",
  paymentAmount: "Payment Amount",
  paymentMethod: "Payment Method",
  selectMethod: "Select payment method",
  amountPlaceholder: "0.00",
  confirmPayment: "Confirm Payment",
  recentPayments: "Recent Payments",
  noPayments: "No Payments",
  noPaymentsDescription: "No payments match your current filters.",
  searchPaymentsPlaceholder: "Search payments...",
  paymentDetails: "Payment Details",
  paymentNumber: "Payment #",
  sapReference: "SAP Reference",
  origin: "Origin",
  portalOrigin: "Portal",
  notes: "Notes",

  // Invoices
  invoicesTitle: "Invoices",
  invoicesSubtitle: "View invoices from SAP",
  invoiceNumber: "Invoice #",
  issueDate: "Date",
  netAmountLabel: "Net Amount",
  vatAmountLabel: "Tax Amount",
  totalLabel: "Total",
  noInvoices: "No Invoices",
  noInvoicesDescription: "No invoices match your current filters.",
  searchInvoicesPlaceholder: "Search invoices...",
  invoiceDetails: "Invoice Details",
  invoiceLines: "Line Items",
  downloadPdf: "Download PDF",
  printInvoice: "Print Invoice",
  invoicePreviewTitle: "Invoice preview",
  invoicePreviewSubtitle: "Review the invoice below. The shared file is a PDF.",
  currency: "Currency",
  unitPrice: "Unit Price",
  lineTotal: "Amount",
  quantity: "Qty",
  material: "Material",
  lineDescription: "Description",
  documentReference: "Document Reference",
  sapCustomerCode: "Customer Code",
  salesOrganization: "Sales Organization",
  documentType: "Document Type",
  amounts: "Amounts",
  allMethods: "All Methods",

  // SAP Balances
  currentBalance: "Current Balance",
  availableContractBalance: "Available Balance",
  lastSynced: "Last synced",
  syncing: "Syncing...",

  // Credit
  creditAccount: "Credit Account",
  availableCredit: "Available Credit",
  creditLimit: "Credit Limit",
  usedCredit: "Used Credit",

  // Due payments
  dueIn30Days: "Due in 30 Days",
  outstandingBalance: "Outstanding Balance",

  // Payment methods
  viban: "VIBAN",
  bankTransfer: "Bank Transfer",
  creditCard: "Credit Card",
  vibanDesc: "Pay via Virtual IBAN",
  bankTransferDesc: "Direct bank transfer",
  creditCardDesc: "Pay with credit card",
  paymentMethods: "Payment Methods",

  // VIBAN
  yourViban: "Your VIBAN",
  copyViban: "Copy VIBAN",
  copied: "Copied!",
  vibanDescription: "Transfer funds to this VIBAN for automatic credit to your account",

  // Stats
  receivable: "Receivable",
  payable: "Payable",
  pending: "Pending",
  overdue: "Overdue",

  // Account status messages
  accountSuspended: "Your account is suspended due to overdue payments. Please contact finance to resolve.",
  accountBlocked: "Your account is blocked. Please contact finance department.",

  // Payment methods (used with dynamic keys)
  methods: {
    viban: "VIBAN Transfer",
    bank: "Bank Transfer",
    card: "Credit Card",
    cash: "Cash",
    check: "Check",
    bank_transfer: "Bank Transfer",
    credit_card: "Credit Card",
    sadad: "SADAD",
  },

  // Form section
  form: {
    name: "Record Name",
    namePlaceholder: "Enter record name",
    status: "Status",
    validation: {
      nameRequired: "Record name is required",
    }
  },

  // Status
  status: {
    active: "Active",
    inactive: "Inactive",
  },

  // Messages
  messages: {
    createSuccess: "Finance record created successfully",
    updateSuccess: "Finance record updated successfully",
    deleteSuccess: "Finance record deleted successfully",
    deleteConfirm: "Are you sure you want to delete this finance record?",
  },

  // Table headers
  table: {
    name: "Name",
    status: "Status",
  },

  // Top-level convenience keys
  name: "Name",
  namePlaceholder: "Enter record name",
  statusLabel: "Status",
  nameRequired: "Record name is required",
  active: "Active",
  inactive: "Inactive",
  createSuccess: "Finance record created successfully",
  updateSuccess: "Finance record updated successfully",
  deleteSuccess: "Finance record deleted successfully",
  deleteConfirm: "Are you sure you want to delete this finance record?",

  // Retry SAP sync
  retrySapSync: "Retry SAP Sync",
  syncingPayment: "Syncing...",
  retrySapSyncDescription: "Retry sending this payment to SAP",

  // Payment method top-level keys
  bank: "Bank Transfer",
  card: "Credit Card",
  cash: "Cash",
  check: "Check",
}
